import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Loader2, Presentation, Save, Sparkles, Wand2 } from 'lucide-react';
import { ouvrirPdf } from '../components/reglement-interieur/SignerReglementDialog';
import { ChoixPhotosDialog } from '../components/livret-base/ChoixPhotosDialog';
import { DiapoCarte } from '../components/presentation-centre/DiapoCarte';
import { VisuelsDialog } from '../components/presentation-centre/VisuelsDialog';
import { ChoixSession } from '../components/presentation-centre/ChoixSession';
import { ArchivesPresentation } from '../components/presentation-centre/ArchivesPresentation';
import { RecadrageDialog } from '../components/presentation-centre/RecadrageDialog';

const Bouton = ({ onClick, Icone, children, testId, charge }) => (
  <button type="button" onClick={onClick} disabled={charge} data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10 disabled:opacity-60">
    {charge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icone className="h-4 w-4" />} {children}
  </button>
);

const DOCS = {
  'mission-locale': { titre: 'Présentation du centre', texte: "Diaporama pour la Mission Locale, les salons ou les réunions d'information." },
  'jury-plenier': { titre: 'Jury plénier BPJEPS motonautisme', texte: 'Bilan certificatif présenté à la DRAJES : les [crochets] sont à compléter.' },
};
const LISTES = ['puces', 'chiffres', 'col1', 'col2', 'col3'];
const CHAMPS = ['surtitre', 'titre', 'intro', 'notes', 'masquee', ...LISTES];
const nettoyer = (valeurs) => Object.fromEntries(Object.entries(valeurs).map(([k, v]) => [k, {
  ...v, ...Object.fromEntries(LISTES.map((l) => [l, (v[l] || []).filter((x) => x.trim())])),
}]));

export default function PresentationCentre({ doc = 'mission-locale' }) {
  const meta = DOCS[doc];
  const api = `/presentation-centre/${doc}`;
  const [data, setData] = useState(null);
  const [valeurs, setValeurs] = useState({});
  const [photos, setPhotos] = useState({});
  const [choix, setChoix] = useState(null);
  const [cadrages, setCadrages] = useState({});
  const [recadre, setRecadre] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [pptx, setPptx] = useState(false);
  const [prefill, setPrefill] = useState(false);
  const [ordre, setOrdre] = useState([]);
  const [visuels, setVisuels] = useState(false);
  const [session, setSession] = useState('');

  const appliquer = (d) => {
    setData(d);
    setValeurs(Object.fromEntries(d.diapos.map((x) => [x.cle, Object.fromEntries(CHAMPS.map((k) => [k, x[k]]))])));
    setPhotos(d.photos || {});
    setCadrages(d.cadrages || {});
    setOrdre(d.diapos.map((x) => x.cle));
  };
  const charger = useCallback(() => {
    setData(null);
    axios.get(`${api}/config`).then(({ data: d }) => appliquer(d)).catch(() => setData({ erreur: true }));
  }, [api]);
  useEffect(() => { charger(); }, [charger]);

  if (!data) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (data.erreur) return <div className="p-8 text-red-600" data-testid="presentation-erreur">Accès non autorisé ou erreur de chargement.</div>;

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      appliquer((await axios.put(`${api}/config`, { diapos: nettoyer(valeurs), photos, ordre, cadrages })).data);
      toast.success('Présentation enregistrée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setEnvoi(false); }
  };
  const refsDe = (d) => photos[d.cle] ?? d.photos.map((c) => `defaut:${c}`);
  const cadrer = (cle, index, c) => {
    const liste = [...(cadrages[cle] || [])];
    while (liste.length <= index) liste.push(null);
    liste[index] = c;
    setCadrages({ ...cadrages, [cle]: liste });
  };
  const action = async (fn, message) => {
    try {
      await axios.put(`${api}/config`, { diapos: nettoyer(valeurs), photos, ordre, cadrages });
      appliquer(await fn());
      toast.success(message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
  };
  const preremplir = async () => {
    if (!session) { toast.error('Choisissez d\'abord la session'); return; }
    setPrefill(true);
    try {
      const { data: r } = await axios.post(`${api}/prefill`, { diapos: nettoyer(valeurs) }, { params: { session } });
      setValeurs({ ...valeurs, ...r.diapos });
      toast.success(`Chiffres repris de l'appli (${r.resume}) : vérifiez-les puis enregistrez`, { duration: 8000 });
    } catch { toast.error('Pré-remplissage impossible'); }
    finally { setPrefill(false); }
  };
  const telechargerPptx = async () => {
    setPptx(true);
    try {
      const { data: b } = await axios.get(`${api}/pptx`, { responseType: 'blob', timeout: 120000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `${data.nom}.pptx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Impossible de générer le PowerPoint'); }
    finally { setPptx(false); }
  };

  return (
    <div className="space-y-6" data-testid="presentation-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]"><ArrowLeft className="h-4 w-4" /> Tableau de bord</Link>
      <header className="rounded-2xl bg-[#0F172A] p-6 text-white shadow-sm sm:p-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-xl bg-[#8DC63F]/20 p-3"><Presentation className="h-7 w-7 text-[#8DC63F]" /></div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl" data-testid="presentation-titre">{meta.titre}</h1>
          </div>
          {doc === 'jury-plenier' && data.edition && <ChoixSession valeur={session} onChange={setSession} />}
          {doc === 'jury-plenier' && data.edition && (
            <Bouton onClick={preremplir} Icone={Sparkles} charge={prefill} testId="presentation-prefill">Pré-remplir depuis l'appli</Bouton>
          )}
          {doc === 'jury-plenier' && data.edition && (
            <Bouton onClick={() => setVisuels(true)} Icone={Wand2} testId="presentation-visuels">Refaire les visuels FISLApp</Bouton>
          )}
          <Bouton onClick={() => ouvrirPdf(`${api}/pdf`, `${data.nom}.pdf`)} Icone={FileText} testId="presentation-voir-pdf">Voir le PDF</Bouton>
          <Bouton onClick={() => ouvrirPdf(`${api}/pdf`, `${data.nom}.pdf`, 'download')} Icone={Download} testId="presentation-telecharger-pdf">PDF</Bouton>
          <Bouton onClick={telechargerPptx} Icone={Download} charge={pptx} testId="presentation-telecharger-pptx">PowerPoint</Bouton>
        </div>
        <p className="mt-3 text-sm text-white/70">{meta.texte} Mettez à jour chaque année les chiffres, les dates et les photos,
          enregistrez, puis téléchargez la version PowerPoint (modifiable) ou PDF. **texte** = gras.</p>
      </header>
      <div className="grid gap-4">
        {ordre.map((cle) => data.diapos.find((x) => x.cle === cle)).filter(Boolean).map((d, i) => (
          <DiapoCarte key={d.cle} n={i + 1} d={d} api={api} valeur={valeurs[d.cle]} defaut={data.defauts[d.cle]} refs={refsDe(d)} cadrages={cadrages[d.cle] || []}
            edition={data.edition}
            onSlot={(slot) => setChoix({ cle: d.cle, titre: `${d.libelle} · photo ${slot + 1}`, max: 1, slot, d })}
            onRecadrer={(index) => setRecadre({ d, index, n: i + 1 })}
            onChange={(v) => setValeurs({ ...valeurs, [d.cle]: v })}
            onDeplacer={(sens) => {
              const j = i + sens;
              if (j < 0 || j >= ordre.length) return;
              const suite = [...ordre];
              [suite[i], suite[j]] = [suite[j], suite[i]];
              setOrdre(suite);
            }}
            premier={i === 0} dernier={i === ordre.length - 1}
            onDupliquer={() => action(async () => (await axios.post(`${api}/dupliquer`, { cle: d.cle })).data, 'Diapositive dupliquée juste en dessous')}
            onSupprimer={() => window.confirm(`Supprimer la copie « ${d.libelle} » ?`)
              && action(async () => (await axios.delete(`${api}/copies/${d.cle}`)).data, 'Copie supprimée')}
            onPhotos={() => setChoix({ cle: d.cle, titre: d.libelle, max: d.nb_photos })} />
        ))}
      </div>
      <ArchivesPresentation api={api} edition={data.edition} libelleDefaut={doc === 'jury-plenier' && session ? `Session ${session}` : ''}
        avant={() => axios.put(`${api}/config`, { diapos: nettoyer(valeurs), photos, ordre, cadrages }).then(({ data: d }) => appliquer(d))} />
      <VisuelsDialog open={visuels} session={session} onClose={() => setVisuels(false)}
        onFait={(d) => setPhotos((p) => ({ ...p, fislapp: d.photos.fislapp, fislapp_parcours: d.photos.fislapp_parcours }))} />
      {recadre && (
        <RecadrageDialog ouvert api={api} cle={recadre.d.cle} numero={recadre.n} valeur={valeurs[recadre.d.cle]} refs={refsDe(recadre.d)}
          cadrages={cadrages[recadre.d.cle] || []} index={recadre.index} onClose={() => setRecadre(null)}
          onValider={(c) => { cadrer(recadre.d.cle, recadre.index, c); setRecadre(null); toast.info('Pensez à enregistrer pour garder ce cadrage'); }} />
      )}
      <ChoixPhotosDialog open={!!choix} titre={choix?.titre} max={choix?.max || 1}
        choisies={choix ? (choix.slot !== undefined ? [refsDe(choix.d)[choix.slot]].filter((r) => r && !r.startsWith('defaut:')) : photos[choix.cle]?.filter((r) => r && !r.startsWith('defaut:'))) : null}
        onClose={() => setChoix(null)}
        onValider={(ids) => {
          const suite = { ...photos };
          if (choix.slot !== undefined) {
            const liste = [...refsDe(choix.d)];
            while (liste.length <= choix.slot) liste.push('');
            const origine = choix.d.photos[choix.slot];
            if (ids === null) liste[choix.slot] = origine ? `defaut:${origine}` : '';
            else if (!ids.length) liste[choix.slot] = '';
            else [liste[choix.slot]] = ids;
            while (liste.length && !liste[liste.length - 1]) liste.pop();
            suite[choix.cle] = liste;
            cadrer(choix.cle, choix.slot, null);
          } else if (ids === null) delete suite[choix.cle]; else suite[choix.cle] = ids;
          setPhotos(suite); setChoix(null);
          toast.info('Pensez à enregistrer pour mettre à jour la présentation');
        }} />
      {data.edition && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-end gap-4 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
          <span className="flex-1 text-xs text-slate-500" data-testid="presentation-maj">
            {data.maj_le ? `Dernière mise à jour : ${new Date(data.maj_le).toLocaleDateString('fr-FR')}` : 'Textes d\'origine'}
          </span>
          <button type="button" onClick={enregistrer} disabled={envoi} data-testid="presentation-enregistrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#1E293B] disabled:opacity-50">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}
